import { useState, useEffect } from "react";
import { X, Phone, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PHONE_NUMBER = "+919270425109";

const cities = [
  "Delhi NCR", "Mumbai", "Bangalore", "Hyderabad", "Chennai",
  "Pune", "Kolkata", "Ahmedabad", "Jaipur", "Lucknow", "Other",
];

const services = [
  "Patients & Elderly Care",
  "Home Nursing",
  "Nanny / Baby Sitting",
  "Other",
];

export default function AssistancePopup() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", city: "", service: "", bot_field: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const dismissed = sessionStorage.getItem("popup_dismissed");
    if (!dismissed) {
      const timer = setTimeout(() => setOpen(true), 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setOpen(false);
    sessionStorage.setItem("popup_dismissed", "true");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || form.name.trim().length < 2) {
      setError("Please enter your name."); return;
    }
    if (!form.phone.trim() || form.phone.trim().length < 10) {
      setError("Please enter a valid mobile number."); return;
    }
    if (!form.city) {
      setError("Please select a city."); return;
    }
    if (!form.service) {
      setError("Please select a service."); return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        service_required: form.service,
        message: `City: ${form.city}`,
        bot_field: form.bot_field,
      });
      setSuccess(true);
    } catch {
      setError("Something went wrong. Please try calling us.");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />
      <div
        data-testid="assistance-popup"
        className="relative bg-white rounded-3xl w-full max-w-md p-8 shadow-2xl z-10 animate-in fade-in zoom-in-95 duration-300"
      >
        <button
          onClick={handleClose}
          data-testid="popup-close"
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors"
        >
          <X className="w-4 h-4 text-[#475569]" />
        </button>

        {success ? (
          <div className="text-center py-6">
            <div className="w-14 h-14 rounded-full bg-[#14B8A6]/10 flex items-center justify-center mx-auto mb-4">
              <Phone className="w-7 h-7 text-[#14B8A6]" />
            </div>
            <h3 className="font-heading text-xl font-bold text-[#0F172A] mb-2">Thank You!</h3>
            <p className="text-sm text-[#64748B] font-body">Our team will call you back shortly.</p>
            <button onClick={handleClose} className="mt-6 text-sm font-semibold text-[#14B8A6] hover:underline font-body">
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <h3 className="font-heading text-2xl font-bold text-[#0F172A] mb-2">
                Need Assistance? We're Here to Help!
              </h3>
              <p className="text-sm text-[#64748B] font-body">
                Please enter your details, and our team will personally assist you.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="absolute opacity-0 -z-10" aria-hidden="true">
                <input type="text" name="bot_field" tabIndex={-1} autoComplete="off"
                  value={form.bot_field} onChange={(e) => setForm({ ...form, bot_field: e.target.value })} />
              </div>

              <Input
                data-testid="popup-name"
                placeholder="Your Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="h-12 rounded-xl bg-slate-50 border-slate-200 font-body"
              />
              <Input
                data-testid="popup-phone"
                type="tel"
                placeholder="Your Mobile Number"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="h-12 rounded-xl bg-slate-50 border-slate-200 font-body"
              />
              <div className="grid grid-cols-2 gap-4">
                <Select value={form.city} onValueChange={(val) => setForm({ ...form, city: val })}>
                  <SelectTrigger data-testid="popup-city" className="h-12 rounded-xl bg-slate-50 border-slate-200 font-body">
                    <SelectValue placeholder="Select City" />
                  </SelectTrigger>
                  <SelectContent className="z-[70]">
                    {cities.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={form.service} onValueChange={(val) => setForm({ ...form, service: val })}>
                  <SelectTrigger data-testid="popup-service" className="h-12 rounded-xl bg-slate-50 border-slate-200 font-body">
                    <SelectValue placeholder="Select Service" />
                  </SelectTrigger>
                  <SelectContent className="z-[70]">
                    {services.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {error && <p data-testid="popup-error" className="text-sm text-red-500 font-body">{error}</p>}

              <button
                type="submit"
                disabled={loading}
                data-testid="popup-submit"
                className="w-full h-12 rounded-full font-semibold bg-[#14B8A6] text-white hover:bg-[#0D9488] transition-all shadow-lg shadow-teal-500/20 font-body disabled:opacity-60 flex items-center justify-center"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Request Call Back"}
              </button>
            </form>

            <p className="text-xs text-center text-[#94A3B8] font-body mt-4">
              By clicking, you agree to our{" "}
              <Link to="/privacy-policy" onClick={handleClose} className="font-semibold text-[#0F172A] hover:underline">Privacy Policy</Link>
              {" "}and{" "}
              <Link to="/terms-conditions" onClick={handleClose} className="font-semibold text-[#0F172A] hover:underline">Terms &amp; Conditions</Link>
            </p>

            <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-slate-100">
              <span className="text-xs text-[#94A3B8] font-body">or</span>
            </div>
            <a
              href={`tel:${PHONE_NUMBER}`}
              data-testid="popup-call-link"
              className="flex items-center justify-center gap-2 mt-2 text-[#14B8A6] font-semibold font-body hover:underline"
            >
              <div className="w-8 h-8 rounded-full bg-[#14B8A6]/10 flex items-center justify-center">
                <Phone className="w-4 h-4 text-[#14B8A6]" />
              </div>
              Call: +91 9270425109
            </a>
          </>
        )}
      </div>
    </div>
  );
}
